import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, saveAuthTokens, clearAuthTokens } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
  const [checked, setChecked] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setChecked(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    saveAuthTokens(data);
    if (data.mfa_required) return { mfaRequired: true };
    setUser(data.user);
    return { user: data.user };
  };

  const mfaVerify = async (code) => {
    const { data } = await api.post('/auth/mfa/verify', { code });
    saveAuthTokens(data);
    setUser(data.user);
    return data.user;
  };

  const completePasskey = async () => {
    const { data } = await api.get('/auth/me');
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post('/auth/register', payload);
    saveAuthTokens(data);
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    clearAuthTokens();
    setUser(false);
  };

  const updateUser = (patch) => setUser((u) => (u ? { ...u, ...patch } : u));

  const isAdmin = user && user.role && user.role !== 'user';

  return (
    <AuthContext.Provider value={{ user, checked, login, mfaVerify, completePasskey, register, logout, refresh, updateUser, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
